import React, { useState, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * <EditableImage /> - Renderiza una imagen. Si es admin, muestra un botón
 * flotante ✏️ para reemplazarla subiendo un archivo desde la compu/cel.
 *
 * Props:
 *   imageKey    (string, requerido) - ID lógico único en la web
 *   defaultSrc  (string, requerido) - URL fallback si no hay override
 *   alt         (string)
 *   style       (obj)
 *   className   (string)
 *   isAdmin     (bool)
 *   adminPassword (string) - contraseña usada para autorizar en el backend
 *   overrides   (obj) - mapa { key: url } de overrides ya cargados
 *   onUpdated   (fn)  - callback llamado tras un upload exitoso
 *   otros props se pasan al <img>
 */
const EditableImage = ({
  imageKey,
  defaultSrc,
  alt = '',
  style = {},
  className = '',
  isAdmin = false,
  adminPassword = '',
  overrides = {},
  onUpdated,
  ...rest
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const overrideUrl = overrides[imageKey];
  const src = overrideUrl ? `${BACKEND_URL}${overrideUrl}` : defaultSrc;

  const abrirSelector = () => {
    if (!isAdmin || uploading) return;
    fileInputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so re-picking the same file re-triggers
    if (!file) return;

    if (!/^image\/(jpe?g|png|gif|webp)$/i.test(file.type)) {
      alert('Formato no permitido. Usá JPG, PNG, WEBP o GIF.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      alert('La imagen supera 8 MB. Comprimila e intentá de nuevo.');
      return;
    }

    const fd = new FormData();
    fd.append('file', file);
    fd.append('admin_password', adminPassword || '');

    try {
      setUploading(true);
      const r = await axios.post(`${API}/imagenes/${imageKey}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (onUpdated) onUpdated(imageKey, r.data);
    } catch (err) {
      alert('❌ Error al subir la imagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const revertir = async (e) => {
    e.stopPropagation();
    if (!window.confirm('¿Volver a la imagen original?')) return;
    try {
      await axios.delete(`${API}/imagenes/${imageKey}?admin_password=${encodeURIComponent(adminPassword || '')}`);
      if (onUpdated) onUpdated(imageKey, null);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.detail || err.message));
    }
  };

  const wrapperStyle = {
    position: 'relative',
    display: 'inline-block',
    width: style.width || '100%',
    lineHeight: 0,
  };

  return (
    <span style={wrapperStyle} data-testid={`editable-img-wrapper-${imageKey}`}>
      <img src={src} alt={alt} style={style} className={className} {...rest} />
      {isAdmin && (
        <>
          <button
            type="button"
            onClick={abrirSelector}
            disabled={uploading}
            title={uploading ? 'Subiendo...' : 'Cambiar imagen'}
            data-testid={`edit-img-btn-${imageKey}`}
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 20,
              padding: '8px 12px',
              background: uploading ? '#999' : 'rgba(0,85,164,0.92)',
              color: 'white',
              border: '2px solid white',
              borderRadius: 999,
              cursor: uploading ? 'wait' : 'pointer',
              fontSize: '0.9em',
              fontWeight: 'bold',
              boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              lineHeight: 1,
            }}
          >
            {uploading ? '⏳' : '✏️'} <span style={{ fontSize: '0.85em' }}>Editar</span>
          </button>
          {overrideUrl && (
            <button
              type="button"
              onClick={revertir}
              title="Volver a la imagen original"
              data-testid={`revert-img-btn-${imageKey}`}
              style={{
                position: 'absolute',
                top: 10,
                right: 110,
                zIndex: 20,
                padding: '8px 10px',
                background: 'rgba(220,53,69,0.9)',
                color: 'white',
                border: '2px solid white',
                borderRadius: 999,
                cursor: 'pointer',
                fontSize: '0.8em',
                fontWeight: 'bold',
                boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                lineHeight: 1,
              }}
            >
              ↺ Original
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleFile}
            style={{ display: 'none' }}
            data-testid={`edit-img-input-${imageKey}`}
          />
        </>
      )}
    </span>
  );
};

export default EditableImage;
